<script lang="ts">
  import { cloneDeep } from "lodash";
  import ActionRequiredDetail from "./ActionRequiredDetail.svelte";
  import Icon from "@shared/svelte/Components/Icon.svelte";
  import LayoutStore from "@shared/svelte/Stores/LayoutStore";
  import DistributorsStore from "@shared/svelte/Stores/DistributorsStore";

  import type { Distributor } from "@shared/types/Distributor";
  import type { OrderGroup } from "@shared/types/OrderGroup";
  import type { RetailerType } from "@shared/types/Retailer";

  export let orderGroup: OrderGroup;
  export let retailer: RetailerType;
  export let setActionCompleted: () => void;

  const distributorsMap = DistributorsStore.mappedDistributors() as { [key: string]: Distributor };
  const showActionRequiredDetail = orderGroup.orders.map(o => o.manual_order_action_required);

  const noActionRequiredDistributorsList = (orderGroup: OrderGroup): string[] => {
    const distributorNames = orderGroup.orders
      .filter(order => !distributorsMap[order.distributor.id]?.manual_order_action_required)
      .map(order => order.distributor.name)
      .sort();

    return cloneDeep(distributorNames);
  };

  const noActionRequiredDistributors = {
    distributorNames: noActionRequiredDistributorsList(orderGroup),
    count: noActionRequiredDistributorsList(orderGroup).length,
    getString: () => {
      const distributorNames = noActionRequiredDistributorsList(orderGroup);

      return [distributorNames.pop(), distributorNames.join(", ")]
        .filter(name => !!name)
        .reverse()
        .join(" and ");
    },
    pluralize: noActionRequiredDistributorsList(orderGroup).length > 1,
  };
</script>

<div class="fizz-inset-vertical-32">
  <span class="fizz-badge fizz-surface-yellow fizz-flex-row-inline fizz-stack-12">
    <Icon color="#FFAB00" type="Warning" className="fizz-icon-16 fizz-inline-4 fizz-icon-warning" />
    Action Required
  </span>
  <h2
    class="fizz-stack-8"
    class:fizz-heading-2={$LayoutStore.isDesktop}
    class:fizz-heading-4={$LayoutStore.isMobile}
  >
    Your order requires one more step
  </h2>
  <p
    class="fizz-stack-8"
    class:fizz-text-large={$LayoutStore.isDesktop}
    class:fizz-text-medium={$LayoutStore.isMobile}
  >
    Orders to these distributors <strong>must be sent</strong> through your email. The buttons below
    make it easy to send.
  </p>
  {#if noActionRequiredDistributors.count}
    <p class="fizz-subdued fizz-text-small fizz-stack-16 fizz-flex-row fizz-align-content-start">
      <Icon
        type="InfoOutline"
        height={16}
        width={12}
        opacity={0.8}
        className="fizz-icon-16 fizz-inline-4"
      />
      Your order{#if noActionRequiredDistributors.pluralize}s{/if} to {noActionRequiredDistributors.getString()}
      {#if noActionRequiredDistributors.pluralize}
        have
      {:else}
        has
      {/if} been sent and
      {#if noActionRequiredDistributors.pluralize}
        do
      {:else}
        does
      {/if} not require further action.
    </p>
  {/if}
  {#each orderGroup.orders as order, i}
    {#if showActionRequiredDetail[i]}
      <ActionRequiredDetail {order} {retailer} on:orderUpdated />
    {/if}
  {/each}
  <div class="fizz-flex-row fizz-justify-end">
    <button
      class="fizz-button fizz-button"
      class:fizz-flex-item={$LayoutStore.isMobile}
      on:click={setActionCompleted}
    >
      Continue to order confirmation
    </button>
  </div>
</div>
